import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface OrbitalMassScaleProps {
  width?: number;
  height?: number;
}

export const OrbitalMassScale: React.FC<OrbitalMassScaleProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [mass, setMass] = useState(1);
  const [distance, setDistance] = useState(80); // Distance radius

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;

    // v = sqrt(G*M / r) -> simplified for simulation: v = sqrt(mass / distance) * factor
    const requiredSpeed = Math.sqrt(mass / distance) * 20; 
    
    // Calculate total angle based on continuous time would require integrating speed,
    // but since mass/distance change dynamically and we want smooth motion, 
    // we should really keep a ref for angle. Since useCanvasAnimation doesn't easily expose a ref to the component body without creating one, 
    // we'll use a hack: attach it to the canvas element or use a closure.
    // For simplicity, we'll just use a pseudo-time based on frameCount and average speed, 
    // which might jump if slider moves fast, but works ok for a simple simulation.
    // Better: use the time to calculate angle based on current speed.
    const angle = frameCount * 0.05 * requiredSpeed;

    const moonX = centerX + Math.cos(angle) * distance;
    const moonY = centerY + Math.sin(angle) * distance;

    // Draw Orbit Path
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.arc(centerX, centerY, distance, 0, Math.PI * 2);
    ctx.stroke();

    // Draw Planet (size scales slightly with mass for visual effect)
    const planetRadius = 15 + Math.sqrt(mass) * 2;
    ctx.fillStyle = '#4A90D9';
    ctx.beginPath();
    ctx.arc(centerX, centerY, planetRadius, 0, Math.PI * 2);
    ctx.fill();

    // Draw Moon
    ctx.fillStyle = '#A0A0A0';
    ctx.beginPath();
    ctx.arc(moonX, moonY, 6, 0, Math.PI * 2);
    ctx.fill();

    // Draw Speed Arrows
    // Tangent vector
    const tx = -Math.sin(angle);
    const ty = Math.cos(angle);
    
    const arrowLength = requiredSpeed * 10;
    
    ctx.beginPath();
    ctx.strokeStyle = '#FDE047';
    ctx.lineWidth = 2;
    ctx.moveTo(moonX, moonY);
    ctx.lineTo(moonX + tx * arrowLength, moonY + ty * arrowLength);
    ctx.stroke();
    
    // Arrow head
    ctx.save();
    ctx.translate(moonX + tx * arrowLength, moonY + ty * arrowLength);
    ctx.rotate(angle + Math.PI / 2); // tangent angle
    ctx.beginPath();
    ctx.fillStyle = '#FDE047';
    ctx.moveTo(0, 0);
    ctx.lineTo(-4, -6);
    ctx.lineTo(4, -6);
    ctx.fill();
    ctx.restore();

    // If mass is high, show "needs more speed!" label
    if (mass > 10) {
      ctx.fillStyle = '#FDE047';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('needs more speed!', moonX + tx * (arrowLength + 15), moonY + ty * (arrowLength + 15));
    }

  }, [size, mass, distance]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 120, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ marginTop: '16px', width: '100%', maxWidth: '300px' }}>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94A3B8' }}>
            <span>Planet Mass: {mass}x</span>
          </div>
          <input 
            type="range" min="1" max="20" value={mass} 
            onChange={(e) => setMass(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94A3B8' }}>
            <span>Moon Distance</span>
          </div>
          <input 
            type="range" min="40" max="140" value={distance} 
            onChange={(e) => setDistance(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </div>
        
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#E2E8F0', marginTop: '4px' }}>
          Heavier planet = faster required orbit
          <br/>
          <span style={{ color: '#4ADE80', fontWeight: 'bold' }}>
            Orbit Speed: {(Math.sqrt(mass / distance) * 100).toFixed(1)} km/s
          </span>
        </div>
      </div>
    </div>
  );
};
